import React, { useEffect, useState } from 'react';
import { motion, useMotionValue, useTransform, animate } from 'framer-motion';
import { TrendingUpIcon, TrendingDownIcon } from 'lucide-react';
interface KPICardProps {
  title: string;
  value: string | number;
  icon: any;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  status?: 'healthy' | 'warning' | 'critical' | 'default';
  delay?: number;
  unit?: string;
}
function AnimatedNumber({
  value
}: {
  value: number;
}) {
  const motionValue = useMotionValue(0);
  const rounded = useTransform(motionValue, latest => Math.round(latest).toLocaleString());
  const [display, setDisplay] = useState('0');
  useEffect(() => {
    const controls = animate(motionValue, value, {
      duration: 1.4,
      ease: [0.16, 1, 0.3, 1]
    });
    const unsub = rounded.on('change', v => setDisplay(v));
    return () => {
      controls.stop();
      unsub();
    };
  }, [value]);
  return <>{display}</>;
}
const statusStyles = {
  healthy: {
    iconBg: 'from-emerald-400/15 to-emerald-500/5',
    iconBorder: 'border-emerald-400/30',
    iconColor: 'text-emerald-300',
    dot: 'bg-emerald-400 text-emerald-400',
    glow: 'bg-emerald-400/10'
  },
  warning: {
    iconBg: 'from-amber-400/15 to-amber-500/5',
    iconBorder: 'border-amber-400/30',
    iconColor: 'text-amber-300',
    dot: 'bg-amber-400 text-amber-400',
    glow: 'bg-amber-400/10'
  },
  critical: {
    iconBg: 'from-red-400/15 to-red-500/5',
    iconBorder: 'border-red-400/30',
    iconColor: 'text-red-300',
    dot: 'bg-red-400 text-red-400',
    glow: 'bg-red-400/10'
  },
  default: {
    iconBg: 'from-cyan-400/15 to-blue-500/5',
    iconBorder: 'border-cyan-400/25',
    iconColor: 'text-cyan-300',
    dot: 'bg-cyan-400 text-cyan-400',
    glow: 'bg-cyan-400/10'
  }
};
export function KPICard({
  title,
  value,
  icon: Icon,
  trend,
  status = 'default',
  delay = 0,
  unit
}: KPICardProps) {
  const s = statusStyles[status];
  const numericValue = typeof value === 'number' ? value : Number(String(value).replace(/[^0-9.-]/g, ''));
  const isNumeric = !isNaN(numericValue) && isFinite(numericValue);
  return <motion.div initial={{
    opacity: 0,
    y: 18
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.5,
    delay,
    ease: [0.16, 1, 0.3, 1]
  }} whileHover={{
    y: -4
  }} className="glass-panel border-shimmer p-5 md:p-6 rounded-2xl relative overflow-hidden group cursor-default" data-id="element-116" data-mp="2dkovuy7ujhat">
      {/* Ambient corner glow */}
      <div className={`absolute -right-16 -top-16 w-48 h-48 ${s.glow} rounded-full blur-3xl group-hover:opacity-150 transition-all duration-700`} data-id="element-117" data-mp="1pucgx3e6okaq" />

      {/* Top section */}
      <div className="flex items-start justify-between mb-5 relative z-10" data-id="element-118" data-mp="1kteyqqt6bq98">
        <div className="flex items-center gap-2" data-id="element-119" data-mp="1fvdew3nxbmwp">
          <span className={`pulse-dot ${s.dot}`} data-id="element-120" data-mp="1awk3qhw407zp" />
          <h3 className="text-slate-400 text-[10px] md:text-xs font-semibold tracking-[0.12em] uppercase" data-id="element-121" data-mp="1axtzh0fhp436">
            {title}
          </h3>
        </div>

        <div className={`p-2.5 rounded-xl bg-gradient-to-br ${s.iconBg} border ${s.iconBorder} ${s.iconColor} shadow-inner relative`} data-id="element-122" data-mp="15rf5t0uqg2f2">
          <Icon size={18} strokeWidth={2.2} data-id="element-123" data-mp="1apgpnnzzg5rl" />
          <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{
          boxShadow: '0 0 18px currentColor',
          color: 'inherit'
        }} data-id="element-124" data-mp="1fqnube6twou1" />
        </div>
      </div>

      {/* Value */}
      <div className="relative z-10" data-id="element-125" data-mp="rzs6es5huds2">
        <div className="flex items-baseline gap-2" data-id="element-126" data-mp="ryqoc3q28r0u">
          <div className="text-3xl md:text-[2rem] font-bold text-white tracking-tight leading-none text-glow font-mono" data-id="element-127" data-mp="wsldhmofpy7b">
            {isNumeric ? <AnimatedNumber value={numericValue} data-id="element-128" data-mp="3vha78fmumkiz" /> : value}
          </div>
          {unit && <span className="text-xs md:text-sm text-slate-500 font-medium" data-id="element-129" data-mp="49ehhai7gv1p">
              {unit}
            </span>}
        </div>

        {trend && <div className={`mt-3 text-xs font-medium flex items-center gap-1.5 ${trend.isPositive ? 'text-emerald-400' : 'text-red-400'}`} data-id="element-130" data-mp="212d2tx5481eq">
            {trend.isPositive ? <TrendingUpIcon size={14} strokeWidth={2.5} data-id="element-131" data-mp="2aygebtluxcec" /> : <TrendingDownIcon size={14} strokeWidth={2.5} data-id="element-132" data-mp="2oqd3bos8b66i" />}
            <span data-id="element-133" data-mp="2osqxzwizygdj">{Math.abs(trend.value)}%</span>
            <span className="text-slate-500 ml-1 hidden md:inline font-normal" data-id="element-134" data-mp="2ko9gk4040f5x">
              vs last period
            </span>
          </div>}
      </div>

      {/* Animated bottom indicator */}
      <div className="absolute bottom-0 left-0 right-0 h-[2px] overflow-hidden" data-id="element-135" data-mp="2tral409bty7h">
        <div className="absolute inset-y-0 left-0 w-full bg-gradient-to-r from-transparent via-cyan-400/60 to-transparent opacity-60 group-hover:opacity-100 transition-opacity" data-id="element-136" data-mp="14a9ikyktqzlb" />
      </div>
    </motion.div>;
}